import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { Icon } from "@/components/Icon";
import { NavigationLinks } from "@/components/workspace/NavigationLinks";
import {
    normalizeNavigationSearch,
    type NavigationArea,
} from "@/components/workspace/navigation";

interface NavigationExplorerProps {
    areas: NavigationArea[];
    areaId?: string | null;
    title?: string;
    favorites: string[];
    onFavorite: (id: string) => void;
    onClose: () => void;
}

export const NavigationExplorer = ({
    areas,
    areaId,
    title,
    favorites,
    onFavorite,
    onClose,
}: NavigationExplorerProps) => {
    const { t } = useTranslation("global");
    const [search, setSearch] = useState("");
    const navigationGrid = useRef<HTMLElement>(null);
    const [cardHeight, setCardHeight] = useState<number>();
    const term = normalizeNavigationSearch(search.trim());
    const sourceAreas = areaId
        ? areas.filter((area) => area.id === areaId)
        : areas;
    const visible = sourceAreas
        .map((area) => ({
            ...area,
            items: area.items.filter((item) =>
                normalizeNavigationSearch(
                    `${area.label} ${item.label}`
                ).includes(term)
            ),
        }))
        .filter((area) => area.items.length);
    const count = visible.reduce((total, area) => total + area.items.length, 0);
    const explorerTitle = title || t("workspace.all_areas");

    useLayoutEffect(() => {
        const grid = navigationGrid.current;

        if (!grid) {
            return;
        }

        const measureCards = (): void => {
            const cards = Array.from(
                grid.querySelectorAll<HTMLElement>(
                    "[data-navigation-area-card]"
                )
            );

            cards.forEach((card) => {
                card.style.height = "auto";
            });

            const height = cards.reduce(
                (maximum, card) =>
                    Math.max(maximum, card.getBoundingClientRect().height),
                0
            );

            setCardHeight(height || undefined);
        };

        const observer = new ResizeObserver(measureCards);

        observer.observe(grid);
        measureCards();

        return () => observer.disconnect();
    }, [visible.map((area) => area.id + ":" + area.items.length).join("|")]);
    return (
        <div className="space-y-4">
            <PageHeader
                compact
                title={explorerTitle}
                action={
                    <div className="flex items-center gap-2">
                        <Link
                            to="/"
                            className="flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium !text-inherit hover:bg-slate-100 dark:hover:bg-white/10"
                        >
                            <Icon value="house" size={16} />
                            {t("workspace.home")}
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            icon="arrowLeft"
                            onClick={onClose}
                        >
                            {t("workspace.back_to_work")}
                        </Button>
                    </div>
                }
            />
            <SearchInput
                value={search}
                onChange={setSearch}
                autoFocus
                aria-label={t("workspace.explore_search")}
                placeholder={t("workspace.explore_search")}
            />
            {/*<p role="status" className="text-xs text-slate-600 dark:text-zinc-300">{t('workspace.explore_count', { count })}</p>*/}
            <nav
                ref={navigationGrid}
                aria-label={explorerTitle}
                className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
            >
                {visible.map((area) => (
                    <section
                        key={area.id}
                        data-navigation-area-card
                        aria-labelledby={`explore-${area.id}`}
                        style={cardHeight ? { height: cardHeight } : undefined}
                        className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-dark-box"
                    >
                        <h2
                            id={`explore-${area.id}`}
                            className="mb-2 flex items-center gap-2 px-3 py-2 text-sm font-semibold"
                        >
                            <Icon value={area.icon} size={17} />
                            <span>{area.label}</span>
                            <Badge
                                variant="primary"
                                size="sm"
                                border={false}
                                count
                                value={area.items.length}
                                className="ml-auto"
                            />
                        </h2>
                        <NavigationLinks
                            items={area.items}
                            favorites={favorites}
                            onFavorite={onFavorite}
                        />
                    </section>
                ))}
            </nav>
            {!count && (
                <p className="py-12 text-center text-sm text-slate-600 dark:text-zinc-300">
                    {t("workspace.search_empty")}
                </p>
            )}
        </div>
    );
};
